import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ResponseModel } from '@dental-ui/account/models';
import { buildApiUrl, buildRequestHeaders } from '@dental-ui/shared/consts';

export interface PackageDto {
  id: string;
  name: string;
  isActive: boolean;
  isLab: boolean;
  isClinic: boolean;
  isCad: boolean;
  description: string | null;
  currencyType: string;
  monthlyPrice: number;
  yearlyPrice: number;
  monthlyDiscount: number | null;
  yearlyDiscount: number | null;
  totalMonthlyPrice: number;
  totalYearlyPrice: number;
  ModifiedBy?: number;
  IsDeleted?: boolean;
}

export interface PackageFeatureDto {
  id: string;
  packageId: string;
  packageName: string;
  isLab: boolean;
  isClinic: boolean;
  isCad: boolean;
  featureId: string;
  featureName: string;
  modifiedBy?: number;
  isDeleted?: boolean;
}

export interface FeatureItem {
  id: string;
  name: string;
  code?: string;
  isActive: boolean;
}

export interface PaginationData<T> {
  data: T[];
  totalPage: number;
  totalRecord: number;
}

@Injectable({ providedIn: 'root' })
export class PortalPackagesApiService {
  private readonly http = inject(HttpClient);

  getPackages(page: number, pageSize: number, name?: string): Observable<PaginationData<PackageDto>> {
    const body = {
      PaginationRequestModel: { page: page + 1, numberOfRecord: pageSize, sortColumn: '', sortOrder: '' },
      PackageFilterRequestDto: { name: name ?? '' },
    };
    return this.http
      .post<ResponseModel<PaginationData<PackageDto>>>(
        buildApiUrl('package.getAllByFilter'),
        body,
        { headers: buildRequestHeaders(true) }
      )
      .pipe(map((res) => res.data ?? { data: [], totalPage: 0, totalRecord: 0 }));
  }

  createPackage(model: Omit<PackageDto, 'id'>): Observable<unknown> {
    return this.http.post(
      buildApiUrl('package.create'),
      model,
      { headers: buildRequestHeaders(true) }
    );
  }

  updatePackage(model: PackageDto): Observable<unknown> {
    return this.http.put(
      buildApiUrl('package.update'),
      model,
      { headers: buildRequestHeaders(true) }
    );
  }

  deletePackage(id: string): Observable<unknown> {
    return this.http.delete(
      `${buildApiUrl('package.delete')}?package=${id}`,
      { headers: buildRequestHeaders(true) }
    );
  }

  getPackageFeatures(packageName: string, page: number, pageSize: number): Observable<PaginationData<PackageFeatureDto>> {
    const body = {
      PaginationRequestModel: { page: page + 1, numberOfRecord: pageSize, sortColumn: '', sortOrder: '' },
      PackageFeatureFilterRequestDto: { packageName },
    };
    return this.http
      .post<ResponseModel<PaginationData<PackageFeatureDto>>>(
        buildApiUrl('packageFeature.getAllByFilter'),
        body,
        { headers: buildRequestHeaders(true) }
      )
      .pipe(map((res) => res.data ?? { data: [], totalPage: 0, totalRecord: 0 }));
  }

  createPackageFeature(packageId: string, featureId: string): Observable<unknown> {
    return this.http.post(
      buildApiUrl('packageFeature.create'),
      { packageId, featureId },
      { headers: buildRequestHeaders(true) }
    );
  }

  deletePackageFeature(id: string): Observable<unknown> {
    return this.http.delete(
      `${buildApiUrl('packageFeature.delete')}?packageFeature=${id}`,
      { headers: buildRequestHeaders(true) }
    );
  }

  getAllFeatures(): Observable<FeatureItem[]> {
    return this.http
      .get<ResponseModel<FeatureItem[]>>(
        buildApiUrl('feature.getAll'),
        { headers: buildRequestHeaders(true) }
      )
      .pipe(map((res) => res.data ?? []));
  }
}
